import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import {
  computeMetric,
  getCompareRange,
  getPeriodRange,
  toISTDateString,
  type CompareMode,
  type DateRange,
  type Metric,
  type PeriodKey,
} from '@/lib/periods';
import type { MetricSource } from './useMetric';
import { useOutlets } from './useOutlets';

interface Args {
  source: MetricSource;
  period: PeriodKey;
  compareMode?: CompareMode;
}

export interface OutletRanking {
  outletId: string;
  outletName: string;
  city: string;
  metric: Metric;
  trend: number[];
}

interface Result {
  rankings: OutletRanking[];
  current: DateRange;
  previous: DateRange;
}

async function fetchRange(column: string, range: DateRange) {
  const start = toISTDateString(range.start);
  const end = toISTDateString(new Date(range.end.getTime() - 1));
  const { data, error } = await marketing
    .from('mv_outlet_kpi_daily')
    .select(`outlet_id, date, ${column}`)
    .gte('date', start)
    .lte('date', end);
  if (error) throw error;
  return (data ?? []) as unknown as Array<Record<string, unknown>>;
}

export function useOutletRanking({
  source,
  period,
  compareMode = 'previous',
}: Args) {
  const { data: outlets = [] } = useOutlets();
  const current = getPeriodRange(period === 'custom' ? '7d' : period);
  const previous = getCompareRange(current, compareMode);
  const column = source;

  return useQuery<Result>({
    enabled: outlets.length > 0,
    queryKey: ['outlet-ranking', source, current.start.toISOString(), previous.start.toISOString()],
    queryFn: async () => {
      const [curRows, prevRows] = await Promise.all([
        fetchRange(column, current),
        fetchRange(column, previous),
      ]);

      const curByOutlet = new Map<string, { total: number; series: Map<string, number> }>();
      for (const r of curRows) {
        const id = String(r.outlet_id);
        const v = Number(r[column] ?? 0);
        const entry = curByOutlet.get(id) ?? { total: 0, series: new Map() };
        entry.total += v;
        entry.series.set(String(r.date), v);
        curByOutlet.set(id, entry);
      }
      const prevByOutlet = new Map<string, number>();
      for (const r of prevRows) {
        const id = String(r.outlet_id);
        prevByOutlet.set(id, (prevByOutlet.get(id) ?? 0) + Number(r[column] ?? 0));
      }

      const rankings: OutletRanking[] = outlets.map((o) => {
        const entry = curByOutlet.get(o.id);
        const prev = prevByOutlet.get(o.id) ?? 0;
        const total = entry?.total ?? 0;
        const trend = entry
          ? Array.from(entry.series.entries())
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([, v]) => v)
          : [];
        return {
          outletId: o.id,
          outletName: o.name,
          city: o.city,
          metric: computeMetric(total, prev),
          trend,
        };
      });

      return { rankings, current, previous };
    },
    staleTime: 60_000,
  });
}

export function pickMovers(rankings: OutletRanking[], n: number): OutletRanking[] {
  return [...rankings]
    .filter((r) => r.metric.comparable && r.metric.previous > 0)
    .sort((a, b) => Math.abs(b.metric.deltaPct) - Math.abs(a.metric.deltaPct))
    .slice(0, n);
}

export function pickDrops(rankings: OutletRanking[], n: number): OutletRanking[] {
  return [...rankings]
    .filter((r) => r.metric.comparable && r.metric.previous > 0 && r.metric.deltaPct < 0)
    .sort((a, b) => a.metric.deltaPct - b.metric.deltaPct)
    .slice(0, n);
}

export function pickSpikes(rankings: OutletRanking[], n: number): OutletRanking[] {
  return [...rankings]
    .filter((r) => r.metric.comparable && r.metric.previous > 0 && r.metric.deltaPct > 0)
    .sort((a, b) => b.metric.deltaPct - a.metric.deltaPct)
    .slice(0, n);
}
